import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import { getAuthLocalStorage } from '../../../../storage/localStorage';
import { ProductsApiService } from '../../../../service/website/productsApiService';
import Logo from '../../../../assets/images/common/importerr-no-image-logo.png';
import { sanitizeUrl } from './utils';

/**
 * Primary image: use first product image, fallback to product.image
 */
const getPrimaryImageFromProduct = (product = {}) => {
  const p = product || {};
  const raw = (Array.isArray(p.imgUrl) && p.imgUrl[0]) || p.image || '';
  return raw ? sanitizeUrl(raw) : '';
};

const getAllImagesFromProduct = (product = {}) => {
  const p = product || {};
  const baseImages = Array.isArray(p.imgUrl) ? p.imgUrl : [];
  return Array.from(
    new Set(
      baseImages
        .filter(Boolean)
        .map(url => sanitizeUrl(url))
        .filter(Boolean)
    )
  );
};

/**
 * Build normalized variant options for the modal
 * Uses current PDP variant structure: variations[] with skuId + skuAttributes[]
 */
const buildVariantOptions = (product = {}) => {
  const p = product || {};
  const rawVariants = Array.isArray(p.variations) ? p.variations : Array.isArray(p.variantList) ? p.variantList : [];

  return rawVariants.map((variant, index) => {
    const skuId = variant.skuId ?? variant.sku ?? index;
    const sku = variant.sku || variant.skuId || '';

    // Normalize attributes from skuAttributes array
    const attrsArray = Array.isArray(variant.skuAttributes) ? variant.skuAttributes : [];

    const attrs = {};
    attrsArray.forEach(attr => {
      const name = attr.attributeNameTrans || attr.attributeName || attr.propertyName || attr.name || String(attr.attributeId || 'Attribute');

      const value = attr.valueTrans || attr.value || attr.propertyValue || attr.valueId || '';

      if (name && value && !attrs[name]) {
        attrs[name] = value;
      }
    });

    // Prefer image on attribute, then variant-level image
    const attrWithImg = attrsArray.find(a => a.skuImageUrl);
    const imageUrl = attrWithImg?.skuImageUrl || variant.skuImageUrl || '';

    // Use finalPrice (FBP) if available, otherwise basic numeric price
    let unitPrice = null;
    if (typeof variant.finalPrice === 'number') {
      unitPrice = variant.finalPrice;
    } else if (typeof variant.price === 'number') {
      unitPrice = variant.price;
    } else if (typeof variant.consignPrice === 'number') {
      unitPrice = variant.consignPrice;
    }

    const labelFromAttrs = Object.values(attrs).filter(Boolean).join(' / ');

    return {
      id: String(skuId),
      sku,
      label: labelFromAttrs || variant.name || variant.title || 'Variant',
      attrs,
      imageUrl,
      unitPrice,
      currency: 'INR',
      raw: variant, // full raw variant for backend
    };
  });
};

const ContactSupplierSection = ({ product = {}, isOpen, setIsOpen }) => {
  const message = useMessage();
  const navigate = useNavigate();

  // Auth
  const authData = getAuthLocalStorage() || {};
  const isLoggedIn = !!(authData.isLoggedIn && authData.loginHeader);

  const [userMessage, setUserMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [variantSelections, setVariantSelections] = useState({});

  // zoom modal state for images
  const [zoomModal, setZoomModal] = useState({
    show: false,
    image: '',
    index: 0,
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Product info for header card
  const productInfo = useMemo(() => {
    const p = product || {};
    const variantCount = Array.isArray(p.variations) ? p.variations.length : 0;

    const productSku = p.sku || p.offerId || p._id || '';
    const primaryImage = getPrimaryImageFromProduct(p);
    const allImages = getAllImagesFromProduct(p);

    // Basic price for admin reference (not shown in UI)
    let price = null;
    if (Array.isArray(p.priceRanges) && p.priceRanges.length && (typeof p.priceRanges[0].finalPrice === 'number' || typeof p.priceRanges[0].price === 'number')) {
      const firstRange = p.priceRanges[0];
      price = typeof firstRange.finalPrice === 'number' ? firstRange.finalPrice : firstRange.price;
    } else if (typeof p.sp === 'number') {
      price = p.sp;
    } else if (typeof p.mrp === 'number') {
      price = p.mrp;
    } else if (typeof p.price === 'number') {
      price = p.price;
    }

    const moqVal = p.moq || p.minOrderQuantity;

    return {
      title: p.title || p.name || 'Product',
      productId: productSku || '-',
      productSku,
      moq: moqVal || '-',
      variantCount,
      imageUrl: primaryImage || allImages[0] || '',
      images: allImages,
      price,
    };
  }, [product]);

  // Flattened variant options from current product
  const variantOptions = useMemo(() => buildVariantOptions(product), [product]);

  // All images available for zoom modal (product + variant images)
  const zoomImages = useMemo(() => {
    const imgs = [];

    if (Array.isArray(productInfo.images) && productInfo.images.length) {
      imgs.push(...productInfo.images.map(i => sanitizeUrl(i)));
    }

    variantOptions.forEach(v => {
      if (v.imageUrl) imgs.push(sanitizeUrl(v.imageUrl));
    });

    return Array.from(new Set(imgs.filter(Boolean)));
  }, [productInfo.images, variantOptions]);

  // main image for mini gallery
  const [mainImage, setMainImage] = useState(productInfo.imageUrl || productInfo.images?.[0] || '');

  useEffect(() => {
    setMainImage(productInfo.imageUrl || productInfo.images?.[0] || '');
  }, [productInfo.imageUrl, productInfo.images]);

  // Derived: total quantity from selected variants
  const totalQuantity = useMemo(() => {
    if (!variantOptions.length) return 0;

    return variantOptions.reduce((sum, v) => {
      const state = variantSelections[v.id];
      if (!state?.checked) return sum;

      const qty = Number(state.quantity);
      if (!Number.isFinite(qty) || qty <= 0) return sum;

      return sum + qty;
    }, 0);
  }, [variantSelections, variantOptions]);

  const resetForm = () => {
    setUserMessage('');
    setVariantSelections({});
    setZoomModal({ show: false, image: '', index: 0 });
  };

  const handleOpen = () => {
    if (!isLoggedIn) {
      message.showToast('To send inquiry please login', ToastTypes.WARNING);
      return;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setIsOpen(false);
    resetForm();
  };

  const handleVariantCheck = (variantId, checked) => {
    setVariantSelections(prev => ({
      ...prev,
      [variantId]: {
        ...(prev[variantId] || {}),
        checked,
      },
    }));
  };

  // quantity change inside row
  const handleVariantQtyChange = (variantId, rawValue) => {
    const cleaned = rawValue.replace(/[^0-9]/g, '');
    const numeric = cleaned ? Number(cleaned) : 0;

    setVariantSelections(prev => ({
      ...prev,
      [variantId]: {
        ...(prev[variantId] || {}),
        quantity: cleaned,
        // auto select when qty > 0
        checked: numeric > 0 ? true : prev[variantId]?.checked || false,
      },
    }));
  };

  // plus/minus buttons
  const changeVariantQtyBy = (variantId, delta) => {
    setVariantSelections(prev => {
      const current = prev[variantId] || {};
      const currentStr = current.quantity || '0';
      const currentNum = /^\d+$/.test(currentStr) ? Number(currentStr) : 0;
      let nextNum = currentNum + delta;
      if (nextNum < 0) nextNum = 0;

      return {
        ...prev,
        [variantId]: {
          ...current,
          quantity: nextNum ? String(nextNum) : '',
          // auto-select when qty > 0
          checked: nextNum > 0 ? true : current.checked || false,
        },
      };
    });
  };

  const handleSelectAllToggle = checked => {
    setVariantSelections(prev => {
      const next = { ...prev };
      variantOptions.forEach(v => {
        const current = next[v.id] || {};
        next[v.id] = { ...current, checked };
      });
      return next;
    });
  };

  const openZoomModal = imageUrl => {
    const sanitized = sanitizeUrl(imageUrl);
    if (!sanitized) return;

    let index = zoomImages.indexOf(sanitized);
    if (index === -1) index = 0;

    setZoomModal({
      show: true,
      image: sanitized,
      index,
    });
  };

  const handleModalClose = () => {
    setZoomModal(prev => ({ ...prev, show: false }));
  };

  const handlePrevImage = () => {
    if (!zoomImages.length) return;
    setZoomModal(prev => {
      const total = zoomImages.length;
      const newIndex = (prev.index - 1 + total) % total;
      return {
        show: true,
        image: zoomImages[newIndex],
        index: newIndex,
      };
    });
  };

  const handleNextImage = () => {
    if (!zoomImages.length) return;
    setZoomModal(prev => {
      const total = zoomImages.length;
      const newIndex = (prev.index + 1) % total;
      return {
        show: true,
        image: zoomImages[newIndex],
        index: newIndex,
      };
    });
  };

  const buildSelectedVariantsPayload = () => {
    if (!variantOptions.length) return [];

    return variantOptions
      .filter(v => variantSelections[v.id]?.checked)
      .map(v => {
        const state = variantSelections[v.id] || {};
        const qtyNum = Number(state.quantity || 0);
        const safeQty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 0;

        const attributes = Object.entries(v.attrs || {}).map(([name, value]) => ({
          name,
          value,
          imageUrl: v.imageUrl || '',
        }));

        return {
          skuId: v.sku || v.id,
          label: v.label,
          imageUrl: v.imageUrl || '',
          unitPrice: v.unitPrice || null,
          quantity: safeQty,
          attributes,
        };
      });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!userMessage.trim()) {
      message.showToast('Please enter your query message', ToastTypes.WARNING);
      return;
    }

    if (!productInfo.productSku) {
      message.showToast('Product reference missing. Please reload page.', ToastTypes.ERROR);
      return;
    }

    // at least one variant selected
    const hasCheckedVariant = variantOptions.some(v => variantSelections[v.id]?.checked);
    if (!hasCheckedVariant) {
      message.showToast('Please select at least one variant.', ToastTypes.WARNING);
      return;
    }

    const variantsPayload = buildSelectedVariantsPayload();
    if (!variantsPayload.length) {
      message.showToast('Please select at least one variant.', ToastTypes.WARNING);
      return;
    }

    if (!totalQuantity || !Number.isFinite(totalQuantity) || totalQuantity <= 0) {
      message.showToast('Please add quantity', ToastTypes.WARNING);
      return;
    }

    const payload = {
      productSku: productInfo.productSku,
      message: userMessage.trim(),
      product: {
        sku: productInfo.productSku,
        title: productInfo.title,
        primaryImage: productInfo.imageUrl || '',
        price: typeof productInfo.price === 'number' ? productInfo.price : null,
        moq: typeof productInfo.moq === 'number' ? productInfo.moq : undefined,
        mode: product?.mode || product?.dsType || '',
      },
      variants: variantsPayload,
      totalQuantity,
    };

    setIsSubmitting(true);
    try {
      const res = await ProductsApiService.submitProductInquiry(payload);

      if (res?.status?.code === 200) {
        message.showToast(res?.status?.message || 'Inquiry submitted successfully', ToastTypes.SUCCESS);
        handleClose();
        return;
      }

      if (res?.status?.code === 402) {
        navigate('/login');
        message.showToast('Session expired. Please login again.', ToastTypes.ERROR);
      } else if (res?.status?.code === 404 && (res?.status?.message || '').toLowerCase().includes('product')) {
        message.showToast(res?.status?.message || 'Product not found.', ToastTypes.ERROR);
      } else {
        message.showToast(res?.status?.message || 'Failed to submit inquiry. Try again.', ToastTypes.ERROR);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Inquiry submit error:', err);
      message.showToast(err?.message || 'Failed to submit inquiry. Try again.', ToastTypes.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allChecked = variantOptions.length > 0 && variantOptions.every(v => variantSelections[v.id]?.checked);

  return (
    <>
      {/* Card section under supplier info */}
      <div className="contact_supplier_section mt-0 mb-3 flex items-center justify-between">
        <div className="text-left">
          <h3 className="contact_header">REQUEST FOR QUOTE</h3>
          <p className="contact_subtext">Share your requirements &amp; get best quote</p>
        </div>

        <div className="contact_buttons" style={{ width: 'auto' }}>
          <button type="button" onClick={handleOpen} className="contact_btn primary" style={{ width: 'auto', whiteSpace: 'nowrap' }}>
            Send Inquiry
          </button>
        </div>
      </div>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-3">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

          {/* Modal content */}
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-[fadeIn_0.2s_ease-out]">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0F172A] to-[#1e293b] px-4 py-3 flex items-center justify-between">
              <div>
                <h2 className="text-[15px] md:text-[16px] font-semibold text-white">Request for quote</h2>
              </div>
              <button type="button" onClick={handleClose} className="text-gray-300 hover:text-white text-xl leading-none">
                ×
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="px-4 py-3 max-h-[80vh] overflow-y-auto custom-scrollbar">
              {/* Product summary + right side variants table */}
              <div className="mb-2 p-3 rounded-xl bg-gray-50 border border-gray-100">
                <div className="flex flex-col md:flex-row gap-3">
                  {/* Left: main product image + horizontal thumbnails */}
                  <div className="md:w-64 w-full">
                    <div className="w-full aspect-square rounded-xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                      {mainImage ? (
                        <img src={sanitizeUrl(mainImage)} alt={productInfo.title} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                      ) : (
                        <img src={Logo} alt="No image" className="w-24 h-24 object-contain opacity-70" />
                      )}
                    </div>

                    {/* Horizontal thumbnails under big image */}
                    {productInfo.images && productInfo.images.length > 1 && (
                      <div className="mt-2 flex gap-1 overflow-x-auto custom-scrollbar">
                        {productInfo.images.slice(0, 8).map((img, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`w-12 h-12 rounded-lg border overflow-hidden bg-white flex-shrink-0 ${
                              sanitizeUrl(img) === mainImage ? 'border-[#3DD0E5]' : 'border-gray-200'
                            }`}
                            onMouseEnter={() => setMainImage(sanitizeUrl(img))}
                            onClick={() => setMainImage(sanitizeUrl(img))}
                          >
                            <img src={sanitizeUrl(img)} alt={`thumb-${idx}`} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right: product info + variants table in same column */}
                  <div className="flex-1 min-w-0 flex flex-col">
                    {/* Product details */}
                    <div>
                      <div className="text-[11px] font-semibold uppercase text-gray-500 mb-1">Product Details</div>
                      <div className="text-[10px] md:text-[11px] font-semibold text-gray-900">{productInfo.title}</div>
                      <div className="flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600 mt-0">
                        <span>
                          <span className="font-medium text-gray-500">Product SKU:</span> {productInfo.productId}
                        </span>
                        <span>
                          <span className="font-medium text-gray-500">MOQ:</span> {productInfo.moq}
                        </span>
                        <span>
                          <span className="font-medium text-gray-500">Variants:</span> {productInfo.variantCount}
                        </span>
                      </div>
                    </div>

                    {/* Variant-wise requirements table */}
                    {variantOptions.length > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[12px] font-medium text-gray-700">
                            Variant-wise requirements <span className="text-red-500">*</span>
                          </label>
                          <span className="hidden sm:inline text-[11px] text-gray-600">Select one or more variants &amp; add quantities.</span>
                        </div>

                        <div className="border rounded-lg border-gray-200 max-h-48 overflow-y-auto overflow-x-auto custom-scrollbar">
                          <table className="min-w-[350px] border-collapse w-full">
                            <thead className="bg-gray-50 sticky top-0 z-[1]">
                              <tr>
                                {/* Select-all checkbox in header */}
                                <th className="px-2 py-1 text-left w-8 md:w-8">
                                  <input type="checkbox" className="rounded border-gray-300" checked={allChecked} onChange={e => handleSelectAllToggle(e.target.checked)} />
                                </th>
                                <th className="px-2 py-1 text-left">Variant</th>
                                {/* <th className="px-2 py-1 text-center text-[11px] w-8 md:w-24">
                                  Price
                                </th> */}
                                <th className="px-2 py-1 text-center text-[11px] w-8 md:w-24">Qty</th>
                              </tr>
                            </thead>
                            <tbody>
                              {variantOptions.map(v => {
                                const state = variantSelections[v.id] || {};
                                const unitPrice = typeof v.unitPrice === 'number' ? v.unitPrice : null;

                                const rawVariant = v.raw || {};
                                const hasDimensions =
                                  rawVariant && (rawVariant.weight != null || rawVariant.length != null || rawVariant.width != null || rawVariant.height != null);

                                return (
                                  <tr key={v.id} className="border-t border-gray-100 align-top">
                                    <td className="px-2 py-1 align-top">
                                      <input type="checkbox" className="mt-[3px]" checked={!!state.checked} onChange={e => handleVariantCheck(v.id, e.target.checked)} />
                                    </td>
                                    <td className="px-2 py-1 align-top">
                                      <div className="flex gap-2">
                                        {v.imageUrl ? (
                                          <div className="w-8 h-8 rounded border border-gray-200 bg-white overflow-hidden flex-shrink-0 cursor-pointer">
                                            <img
                                              src={sanitizeUrl(v.imageUrl)}
                                              alt={v.label}
                                              className="w-full h-full object-contain"
                                              referrerPolicy="no-referrer"
                                              onClick={() => openZoomModal(v.imageUrl)}
                                            />
                                          </div>
                                        ) : null}
                                        <div className="min-w-0">
                                          {/* first line: text (label) */}
                                          <div className="font-medium text-gray-800 text-[12px]">{v.label}</div>
                                          {/* second line: weight / dimensions */}
                                          {hasDimensions && (
                                            <div className="text-[11px] text-gray-500 truncate">
                                              {rawVariant.weight != null && (
                                                <>
                                                  Weight: {Number(rawVariant.weight).toFixed(2)}
                                                  kg{' '}
                                                </>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    {/* <td className="px-2 py-1 align-top text-center text-[12px] text-gray-800">
                                      {unitPrice
                                        ? `₹${unitPrice.toLocaleString()}`
                                        : "-"}
                                    </td> */}
                                    <td className="px-2 py-1 align-top">
                                      <div className="inline-flex items-center border border-gray-300 rounded-md bg-white overflow-hidden">
                                        <button type="button" className="w-6 h-6 flex items-center justify-center text-[14px]" onClick={() => changeVariantQtyBy(v.id, -1)}>
                                          -
                                        </button>
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          pattern="[0-9]*"
                                          className="w-6 px-0 border-0 text-[12px] text-center outline-none"
                                          placeholder="0"
                                          value={state.quantity || ''}
                                          onChange={e => handleVariantQtyChange(v.id, e.target.value)}
                                        />
                                        <button type="button" className="w-6 h-6 flex items-center justify-center text-[14px]" onClick={() => changeVariantQtyBy(v.id, +1)}>
                                          +
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-1 flex justify-between items-center">
                          <p className="text-[11px] text-gray-500">Select at least one variant and share approximate quantity.</p>

                          {/* Total Qty Display */}
                          <span className="text-[11px] font-semibold text-gray-700 pr-2">Total Quantity: {totalQuantity || 0}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="mb-2">
                <label className="block text-[12px] font-medium text-gray-700 mb-1">
                  Your Query / Requirements <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={userMessage}
                  onChange={e => setUserMessage(e.target.value)}
                  rows={2}
                  placeholder="Example: I want 200 pieces. Please share best price & timeline."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] resize-y focus:outline-none focus:ring-1 focus:ring-[#3DD0E5] focus:border-transparent"
                />
              </div>

              {/* Footer buttons */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-1.5 rounded-full border border-gray-300 text-[13px] text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-1.5 rounded-full bg-black text-white text-[13px] font-semibold hover:bg-[#24b5cb] disabled:opacity-60 disabled:cursor-not-allowed shadow-sm w-full sm:w-auto"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image zoom modal (same UX as before) */}
      {zoomModal.show && (
        <div className="zoom_modal" onClick={handleModalClose}>
          <div className="zoom_modal_content" onClick={e => e.stopPropagation()}>
            <button className="modal_close_btn" onClick={handleModalClose}>
              ×
            </button>
            <button
              className="modal_nav_btn modal_prev"
              onClick={e => {
                e.stopPropagation();
                handlePrevImage();
              }}
            >
              ‹
            </button>
            <img src={zoomModal.image} alt="Zoomed image" className="zoom_modal_image" referrerPolicy="no-referrer" />
            <button
              className="modal_nav_btn modal_next"
              onClick={e => {
                e.stopPropagation();
                handleNextImage();
              }}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactSupplierSection;
